import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price';

import { useShippingPrice } from '../hooks/useShippingPrice.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

import { type ProductShipping_viewer$key } from './__generated__/ProductShipping_viewer.graphql.ts';
import { type ProductShipping_item$key } from './__generated__/ProductShipping_item.graphql.ts';

const viewerFragment = graphql`
    fragment ProductShipping_viewer on Viewer {
        ...useShippingPriceV2_viewer
    }
`;
const itemFragment = graphql`
    fragment ProductShipping_item on Item {
        ...useShippingPriceV2_item
    }
`;

type ProductShippingProps = {
    viewer: ProductShipping_viewer$key | null | undefined;
    item: ProductShipping_item$key;
    currency: string;
    shippingPrices?: Record<string, number> | null;
    showShippingCost?: boolean;
    isShippingCostSbVariant?: boolean;
};

const FreeShippingText: FC = () => (
    <span className={classnames(dibsCss.textBodyXSmallHeavy, dibsCss.textInternalsecondary)}>
        <FormattedMessage id="dc.buyerProductTile.freeShipping" defaultMessage="Free Shipping" />
    </span>
);

const ProductShipping: FC<ProductShippingProps> = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const amount = useShippingPrice({ item, viewer, currency });
    if (amount === 0) {
        return (
            <p className={dibsCss.mt2px}>
                <FreeShippingText />
            </p>
        );
    }
    return null;
};

type ProductShippingInfoProps = {
    shippingPrices?: Record<string, number> | null;
    currency: string;
};

const ProductShippingInfo: FC<ProductShippingInfoProps> = ({ shippingPrices, currency }) => {
    // shippingPrices are not defined when tile is not supposed to have shipping cost at all
    // shippingPrices are null in two cases:
    // - are not yet loaded
    // - loading was skipped, e.g. for sold items
    if (!shippingPrices) {
        return null;
    }

    const amount = shippingPrices[currency] ?? null;

    return (
        <p className={dibsCss.mt2px}>
            {amount === 0 ? (
                <FreeShippingText />
            ) : (
                <span data-tn="shipping-cost" className={dibsCss.sassyFontBodyXSmall}>
                    {amount === null ? (
                        <FormattedMessage
                            id="dc.buyerProductTile.shippingRatesVary"
                            defaultMessage="Shipping rates vary"
                        />
                    ) : (
                        <FormattedMessage
                            id="dc.buyerProductTile.shippingPrice"
                            defaultMessage="{price} shipping"
                            values={{
                                price: <FormattedPrice currency={currency} value={amount} />,
                            }}
                        />
                    )}
                </span>
            )}
        </p>
    );
};

export const ProductShippingWrapper: FC<ProductShippingProps & ProductShippingInfoProps> = ({
    viewer,
    item,
    currency,
    isShippingCostSbVariant,
    shippingPrices,
}) => {
    return isShippingCostSbVariant ? (
        <ProductShippingInfo shippingPrices={shippingPrices} currency={currency} />
    ) : (
        <ProductShipping viewer={viewer} item={item} currency={currency} />
    );
};
